"use client";

import { useRef, useState, useEffect, type FC, type Ref } from "react";
import {
  Carousel,
  type CarouselRef,
  ContentMenu,
  ContentMenuProvider,
  mergeRefs,
  useMaybePubSub,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";

import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components";

export type CarouselTemplateProps = {
  /**
   * Templates to render.
   */
  templates: TemplateCollection;
  /**
   * Navigation type for the carousel
   * - "pagination": Shows a numbered navigation menu above the carousel
   */
  navigation?: "pagination";
  /**
   * Layout variant for the carousel.
   * - "full": Full-viewport height with pagination navigation and filler centering (default), ideal for "active" cards.
   * - "compact": No min-height, no paginationr — items flow within the container width.
   */
  variant?: "full" | "compact";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<CarouselRef>;
};

export const CarouselTemplate: FC<CarouselTemplateProps> = ({
  templates,
  navigation = "pagination",
  variant = "full",
  ref,
}) => {
  const carouselRef = useRef<CarouselRef>(null);
  const pubSub = useMaybePubSub();
  const [activeIndex, setActiveIndex] = useState(0);
  const isCompact = variant === "compact";

  useEffect(() => {
    if (!pubSub) return;

    const unsubscribe = pubSub.subscribe("scrollToTop", ({ tag }) => {
      if (tag === "carousel") {
        carouselRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });

    return () => unsubscribe();
  }, [pubSub]);

  const content = (
    <Carousel
      ref={mergeRefs(carouselRef, ref)}
      variant={isCompact ? "default" : "centered"}
      minHeight={isCompact ? undefined : `calc(100dvh - ${rem(140)})`}
      filler
      onIndexChange={isCompact ? undefined : setActiveIndex}
      navigation={
        !isCompact && navigation === "pagination" ? (
          <ContentMenu
            mode="carousel"
            carouselRef={carouselRef}
            activeIndex={activeIndex}
          />
        ) : undefined
      }
    >
      {templates && templates.length > 0 && (
        <TemplatesParser templates={templates} level={1} />
      )}
    </Carousel>
  );

  if (!isCompact && navigation === "pagination") {
    return <ContentMenuProvider>{content}</ContentMenuProvider>;
  }

  return content;
};
